import { Link } from "react-router-dom";
import type { CaseItem } from "@/mocks/cases";
import VerdictBadge from "@/components/base/VerdictBadge";
import Tag from "@/components/base/Tag";

interface CaseCardProps {
  item: CaseItem;
  variant?: "default" | "featured" | "compact";
}

export default function CaseCard({ item, variant = "default" }: CaseCardProps) {
  const isFeatured = variant === "featured";
  const isCompact = variant === "compact";

  return (
    <article
      className={`group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 ${
        isCompact ? "" : "h-full"
      }`}
    >
      <Link
        to={`/cases/${item.slug}`}
        className="relative block w-full overflow-hidden bg-background-200"
        aria-label={item.title}
      >
        <div className={`w-full ${isFeatured ? "h-56 md:h-72" : isCompact ? "h-36" : "h-44"}`}>
          <img
            src={item.cover}
            alt={item.coverAlt}
            title={`${item.title} - 真相核验`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          <VerdictBadge verdict={item.status} />
        </div>
      </Link>

      <div className={`flex flex-1 flex-col ${isCompact ? "p-4" : "p-5"}`}>
        <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Tag tone="secondary" icon="ri-price-tag-3-line">
            {item.category}
          </Tag>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line text-sm" aria-hidden="true" />
            {item.region}
          </span>
        </div>

        <h3
          className={`mt-3 font-heading font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 ${
            isFeatured ? "text-xl md:text-2xl" : isCompact ? "text-base" : "text-lg"
          }`}
        >
          <Link to={`/cases/${item.slug}`}>{item.title}</Link>
        </h3>

        {!isCompact ? (
          <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-foreground-600">
            {item.summary}
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-sm" aria-hidden="true" />
            更新于 {item.updateDate}
          </span>
          <span className="flex items-center gap-1.5 text-accent-700">
            <i className="ri-fire-line text-sm" aria-hidden="true" />
            热度 {item.heat.toLocaleString()}
          </span>
        </div>
      </div>
    </article>
  );
}